// Pricing3.js - Contains functions for creating a comparison pricing section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Pricing section with a comparison table
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the pricing section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The pricing section elements
 */
export const createPricingElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const pricingSectionClass = 'pricing3-section';
  const pricingContainerClass = 'pricing3-container';
  const pricingHeaderClass = 'pricing3-header';
  const pricingTitleClass = 'pricing3-title';
  const pricingSubtitleClass = 'pricing3-subtitle';
  const pricingTableClass = 'pricing3-table';
  const pricingTableHeaderClass = 'pricing3-table-header';
  const pricingTableBodyClass = 'pricing3-table-body';
  const pricingColumnClass = 'pricing3-column';
  const pricingFeatureRowClass = 'pricing3-feature-row';
  const pricingFeatureLabelClass = 'pricing3-feature-label';
  const pricingFeatureItemClass = 'pricing3-feature-item';
  const pricingPlanHeaderClass = 'pricing3-plan-header';
  const pricingPlanHeaderBasicClass = 'pricing3-plan-header-basic';
  const pricingPlanHeaderStandardClass = 'pricing3-plan-header-standard';
  const pricingPlanHeaderAdvancedClass = 'pricing3-plan-header-advanced';
  const pricingPlanNameClass = 'pricing3-plan-name';
  const pricingPlanPriceClass = 'pricing3-plan-price';
  const pricingPlanButtonClass = 'pricing3-plan-button';
  const pricingCheckIconClass = 'pricing3-check-icon';
  const pricingXIconClass = 'pricing3-x-icon';
  const pricingTableHeaderRowClass = 'pricing3-table-header-row';
  
  // Desktop Styles (default)
  const pricingSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8f9fa',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const pricingContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '50px'
  };

  const pricingHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '700px'
  };

  const pricingTitleStyles = {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '16px'
  };

  const pricingSubtitleStyles = {
    fontSize: '18px',
    color: '#666666',
    lineHeight: '1.6'
  };

  const pricingTableStyles = {
    width: '100%',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
    backgroundColor: '#ffffff',
    display: 'table',
    borderCollapse: 'collapse',
    borderSpacing: '0'
  };

  const pricingTableHeaderStyles = {
    display: 'table-header-group',
    width: '100%',
    backgroundColor: '#ffffff'
  };

  const pricingTableBodyStyles = {
    display: 'table-row-group',
    width: '100%'
  };

  const pricingColumnStyles = {
    display: 'table-cell',
    padding: '30px 15px',
    borderRight: '1px solid #e0e0e0',
    width: '25%',
    verticalAlign: 'top'
  };

  const pricingFeatureRowStyles = {
    display: 'table-row',
    width: '100%',
    borderBottom: '1px solid #e0e0e0'
  };

  const pricingFeatureLabelStyles = {
    padding: '20px 15px',
    fontSize: '16px',
    color: '#333333',
    fontWeight: '500',
    display: 'table-cell',
    textAlign: 'left',
    borderRight: '1px solid #e0e0e0',
    width: '25%',
    verticalAlign: 'middle'
  };

  const pricingFeatureItemStyles = {
    padding: '15px',
    fontSize: '16px',
    color: '#666666',
    display: 'table-cell',
    textAlign: 'center',
    verticalAlign: 'middle',
    width: '25%'
  };

  const pricingPlanHeaderStyles = {
    textAlign: 'center',
    padding: '30px 15px',
    borderBottom: '1px solid #e0e0e0'
  };

  const pricingPlanNameStyles = {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '10px'
  };

  const pricingPlanPriceStyles = {
    fontSize: '30px',
    fontWeight: 'bold',
    color: '#4361ee',
    flex: 1,
    minWidth: '150px',
    textAlign: 'center',
    padding: '15px 10px',
  };

  const pricingPlanButtonStyles = {
    padding: '10px 20px',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    backgroundColor: '#4361ee',
    color: '#ffffff',
    border: 'none',
    marginTop: '15px',
    width: '100%',
    maxWidth: '200px'
  };

  const pricingCheckIconStyles = {
    width: '20px',
    height: '20px',
    display: 'inline-block'
  };

  const pricingXIconStyles = {
    width: '20px',
    height: '20px',
    display: 'inline-block'
  };
  
  // Tablet Styles
  const tabletPricingSectionStyles = {
    paddingTop: '60px',
    paddingBottom: '60px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const tabletPricingContainerStyles = {
    maxWidth: '100%',
    overflowX: 'auto',
    padding: '0 10px'
  };

  const tabletPricingTableStyles = {
    display: 'block',
    overflow: 'auto',
    width: '100%',
    minWidth: '600px'
  };
  
  const tabletPricingTableHeaderStyles = {
    display: 'block',
    width: '100%'
  };

  const tabletPricingTableHeaderRowStyles = {
    display: 'flex',
    width: '100%',
    minWidth: '600px'
  };

  const tabletPricingColumnStyles = {
    display: 'table-cell',
    padding: '15px 10px',
    minWidth: '150px',
    width: '25%',
    verticalAlign: 'middle',
    textAlign: 'center'
  };
  
  const tabletPricingPlanHeaderBasicStyles = {
    textAlign: 'center',
    padding: '25px 10px',
    borderBottom: '1px solid #e0e0e0',
    width: '25%',
    minWidth: '150px',
    display: 'table-cell',
    borderRight: '1px solid #e0e0e0'
  };

  const tabletPricingPlanHeaderStandardStyles = {
    textAlign: 'center',
    padding: '25px 10px',
    borderBottom: '1px solid #e0e0e0',
    width: '25%',
    minWidth: '150px',
    display: 'table-cell',
    borderRight: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fd'
  };

  const tabletPricingPlanHeaderAdvancedStyles = {
    textAlign: 'center',
    padding: '25px 10px',
    borderBottom: '1px solid #e0e0e0',
    width: '25%',
    minWidth: '150px',
    display: 'table-cell',
    borderRight: 'none'
  };
  
  const tabletPricingTableBodyStyles = {
    display: 'block',
    width: '100%'
  };
  
  const tabletPricingFeatureRowStyles = {
    display: 'flex',
    flexWrap: 'nowrap',
    borderBottom: '1px solid #e0e0e0',
    minWidth: '600px'
  };
  
  const tabletPricingFeatureLabelStyles = {
    padding: '15px 10px',
    fontSize: '15px',
    color: '#333333',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    minWidth: '150px',
    flex: '1',
    borderRight: '1px solid #e0e0e0'
  };
  
  const tabletPricingFeatureItemStyles = {
    padding: '15px 10px',
    fontSize: '15px',
    color: '#666666',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: '150px',
    flex: '1'
  };
  
  // Mobile Styles
  const mobilePricingSectionStyles = {
    paddingTop: '40px',
    paddingBottom: '40px',
    paddingLeft: '10px',
    paddingRight: '10px'
  };

  const mobilePricingContainerStyles = {
    maxWidth: '100%',
    padding: '0'
  };

  const mobilePricingTableStyles = {
    display: 'block',
    overflow: 'auto',
    width: '100%',
    margin: '0',
    WebkitOverflowScrolling: 'touch'
  };
  
  const mobilePricingTableHeaderStyles = {
    display: 'block',
    width: '100%',
    minWidth: '600px'
  };
  
  const mobilePricingTableBodyStyles = {
    display: 'block',
    width: '100%'
  };
  
  const mobilePricingFeatureRowStyles = {
    display: 'flex',
    flexWrap: 'nowrap',
    minWidth: '600px',
    borderBottom: '1px solid #e0e0e0'
  };
  
  const mobilePricingFeatureLabelStyles = {
    minWidth: '150px',
    flex: '1',
    padding: '12px 8px',
    fontSize: '14px',
    fontWeight: '500',
    borderRight: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center'
  };
  
  const mobilePricingFeatureItemStyles = {
    minWidth: '150px',
    flex: '1',
    padding: '12px 8px',
    fontSize: '14px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const mobilePricingTableWrapperStyles = {
    width: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    msOverflowStyle: '-ms-autohiding-scrollbar',
    position: 'relative',
    left: '0',
    paddingBottom: '10px'
  };
  
  const mobilePricingPlanHeaderBasicStyles = {
    padding: '20px 10px',
    fontSize: '16px',
    minWidth: '150px'
  };

  const mobilePricingPlanHeaderStandardStyles = {
    padding: '20px 10px',
    fontSize: '16px',
    minWidth: '150px'
  };

  const mobilePricingPlanHeaderAdvancedStyles = {
    padding: '20px 10px',
    fontSize: '16px',
    minWidth: '150px'
  };
  
  const mobilePricingTableHeaderRowStyles = {
    display: 'flex',
    width: '100%',
    minWidth: '600px'
  };
  
  const mobilePlanButtonStyles = {
    padding: '8px 15px',
    fontSize: '14px',
    maxWidth: '130px'
  };
  
  const pricingPlanHeaderBasicStyles = {
    textAlign: 'center',
    padding: '30px 15px',
    borderBottom: '1px solid #e0e0e0',
    width: '25%',
    display: 'table-cell',
    borderRight: '1px solid #e0e0e0'
  };

  const pricingPlanHeaderStandardStyles = {
    textAlign: 'center',
    padding: '30px 15px',
    borderBottom: '1px solid #e0e0e0',
    width: '25%',
    display: 'table-cell',
    borderRight: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fd'
  };

  const pricingPlanHeaderAdvancedStyles = {
    textAlign: 'center',
    padding: '30px 15px',
    borderBottom: '1px solid #e0e0e0',
    width: '25%',
    display: 'table-cell',
    borderRight: 'none'
  };
  
  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(pricingSectionClass, pricingSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingContainerClass, pricingContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingHeaderClass, pricingHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingTitleClass, pricingTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingSubtitleClass, pricingSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingTableClass, pricingTableStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingTableHeaderClass, pricingTableHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingTableBodyClass, pricingTableBodyStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingColumnClass, pricingColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeatureRowClass, pricingFeatureRowStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeatureLabelClass, pricingFeatureLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingFeatureItemClass, pricingFeatureItemStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingPlanHeaderClass, pricingPlanHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingPlanHeaderBasicClass, pricingPlanHeaderBasicStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingPlanHeaderStandardClass, pricingPlanHeaderStandardStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingPlanHeaderAdvancedClass, pricingPlanHeaderAdvancedStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingPlanNameClass, pricingPlanNameStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingPlanPriceClass, pricingPlanPriceStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingPlanButtonClass, pricingPlanButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingCheckIconClass, pricingCheckIconStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingXIconClass, pricingXIconStyles);
    newRegistry[DEVICES.DESKTOP].set(pricingTableHeaderRowClass, {});

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(pricingSectionClass, tabletPricingSectionStyles);
    newRegistry[DEVICES.TABLET].set(pricingContainerClass, tabletPricingContainerStyles);
    newRegistry[DEVICES.TABLET].set(pricingTableClass, tabletPricingTableStyles);
    newRegistry[DEVICES.TABLET].set(pricingTableHeaderClass, tabletPricingTableHeaderStyles);
    newRegistry[DEVICES.TABLET].set(pricingTableBodyClass, tabletPricingTableBodyStyles);
    newRegistry[DEVICES.TABLET].set(pricingFeatureRowClass, tabletPricingFeatureRowStyles);
    newRegistry[DEVICES.TABLET].set(pricingColumnClass, tabletPricingColumnStyles);
    newRegistry[DEVICES.TABLET].set(pricingPlanHeaderBasicClass, tabletPricingPlanHeaderBasicStyles);
    newRegistry[DEVICES.TABLET].set(pricingPlanHeaderStandardClass, tabletPricingPlanHeaderStandardStyles);
    newRegistry[DEVICES.TABLET].set(pricingPlanHeaderAdvancedClass, tabletPricingPlanHeaderAdvancedStyles);
    newRegistry[DEVICES.TABLET].set(pricingTableHeaderRowClass, tabletPricingTableHeaderRowStyles);
    newRegistry[DEVICES.TABLET].set(pricingFeatureLabelClass, tabletPricingFeatureLabelStyles);
    newRegistry[DEVICES.TABLET].set(pricingFeatureItemClass, tabletPricingFeatureItemStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(pricingSectionClass, mobilePricingSectionStyles);
    newRegistry[DEVICES.MOBILE].set(pricingContainerClass, mobilePricingContainerStyles);
    newRegistry[DEVICES.MOBILE].set(pricingTableClass, mobilePricingTableStyles);
    newRegistry[DEVICES.MOBILE].set(pricingTableHeaderClass, mobilePricingTableHeaderStyles);
    newRegistry[DEVICES.MOBILE].set(pricingTableBodyClass, mobilePricingTableBodyStyles);
    newRegistry[DEVICES.MOBILE].set(pricingFeatureRowClass, mobilePricingFeatureRowStyles);
    newRegistry[DEVICES.MOBILE].set(pricingFeatureLabelClass, mobilePricingFeatureLabelStyles);
    newRegistry[DEVICES.MOBILE].set(pricingFeatureItemClass, mobilePricingFeatureItemStyles);
    newRegistry[DEVICES.MOBILE].set(pricingPlanHeaderBasicClass, mobilePricingPlanHeaderBasicStyles);
    newRegistry[DEVICES.MOBILE].set(pricingPlanHeaderStandardClass, mobilePricingPlanHeaderStandardStyles);
    newRegistry[DEVICES.MOBILE].set(pricingPlanHeaderAdvancedClass, mobilePricingPlanHeaderAdvancedStyles);
    newRegistry[DEVICES.MOBILE].set(pricingTableHeaderRowClass, mobilePricingTableHeaderRowStyles);
    newRegistry[DEVICES.MOBILE].set(pricingPlanButtonClass, mobilePlanButtonStyles);
    
    // Add a wrapper for the table
    newRegistry[DEVICES.MOBILE].set('pricing-table-wrapper', mobilePricingTableWrapperStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    pricingSectionClass,
    pricingContainerClass,
    pricingHeaderClass,
    pricingTitleClass,
    pricingSubtitleClass,
    pricingTableClass,
    pricingTableHeaderClass,
    pricingTableBodyClass,
    pricingColumnClass,
    pricingFeatureRowClass,
    pricingFeatureLabelClass,
    pricingFeatureItemClass,
    pricingPlanHeaderClass,
    pricingPlanHeaderBasicClass,
    pricingPlanHeaderStandardClass,
    pricingPlanHeaderAdvancedClass,
    pricingPlanNameClass,
    pricingPlanPriceClass,
    pricingPlanButtonClass,
    pricingCheckIconClass,
    pricingXIconClass,
    pricingTableHeaderRowClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  
  // Header elements
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  
  // Table container
  const tableWrapperId = Date.now() + 5;
  const tableId = Date.now() + 6;
  const tableHeaderId = Date.now() + 7;
  const tableHeaderRowId = Date.now() + 8;
  const tableBodyId = Date.now() + 9;
  
  // Header cells
  const emptyHeaderId = Date.now() + 10;
  const basicHeaderId = Date.now() + 11;
  const standardHeaderId = Date.now() + 12;
  const advancedHeaderId = Date.now() + 13;
  
  // Price Row
  const priceRowId = Date.now() + 14;
  const priceLabelId = Date.now() + 15;
  const basicPriceId = Date.now() + 16;
  const standardPriceId = Date.now() + 17;
  const advancedPriceId = Date.now() + 18;
  
  // Storage Row
  const storageRowId = Date.now() + 19;
  const storageLabelId = Date.now() + 20;
  const basicStorageId = Date.now() + 21;
  const standardStorageId = Date.now() + 22;
  const advancedStorageId = Date.now() + 23;
  
  // People Row
  const peopleRowId = Date.now() + 24;
  const peopleLabelId = Date.now() + 25;
  const basicPeopleId = Date.now() + 26;
  const standardPeopleId = Date.now() + 27;
  const advancedPeopleId = Date.now() + 28;
  
  // Fraud Row
  const fraudRowId = Date.now() + 29;
  const fraudLabelId = Date.now() + 30;
  const basicFraudId = Date.now() + 31;
  const standardFraudId = Date.now() + 32;
  const advancedFraudId = Date.now() + 33;
  
  // Fraud Icons
  const basicFraudIconId = Date.now() + 34;
  const standardFraudIconId = Date.now() + 35;
  const advancedFraudIconId = Date.now() + 36;
  
  // Support Row
  const supportRowId = Date.now() + 37;
  const supportLabelId = Date.now() + 38;
  const basicSupportId = Date.now() + 39;
  const standardSupportId = Date.now() + 40;
  const advancedSupportId = Date.now() + 41;
  
  // Support Icons
  const basicSupportIconId = Date.now() + 42;
  const standardSupportIconId = Date.now() + 43;
  const advancedSupportIconId = Date.now() + 44;
  
  // Customization Row
  const customizationRowId = Date.now() + 45;
  const customizationLabelId = Date.now() + 46;
  const basicCustomizationId = Date.now() + 47;
  const standardCustomizationId = Date.now() + 48;
  const advancedCustomizationId = Date.now() + 49;
  
  // Customization Icons
  const basicCustomizationIconId = Date.now() + 50;
  const standardCustomizationIconId = Date.now() + 51;
  const advancedCustomizationIconId = Date.now() + 52;
  
  // Button Row
  const buttonRowId = Date.now() + 53;
  const buttonLabelId = Date.now() + 54;
  const basicButtonId = Date.now() + 55;
  const standardButtonId = Date.now() + 56;
  const advancedButtonId = Date.now() + 57;
  
  // Button Contents
  const basicButtonContentId = Date.now() + 58;
  const standardButtonContentId = Date.now() + 59;
  const advancedButtonContentId = Date.now() + 60;
  
  const newElements = [
    // Main Pricing Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Pricing Table Section',
      parentId: null,
      className: pricingSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'pricing-section',
        'data-component': 'section',
        'data-section-type': 'pricing',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: pricingContainerClass,
      styles: {},
      children: [headerId, tableWrapperId],
      text: '',
      attributes: {
        'data-element': 'pricing-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pricing Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: pricingHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'pricing-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Pricing Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: pricingTitleClass,
      styles: {},
      children: [],
      text: 'Pricing Table Template',
      attributes: {
        'data-element': 'pricing-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Pricing Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: pricingSubtitleClass,
      styles: {},
      children: [],
      text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      attributes: {
        'data-element': 'pricing-subtitle'
      }
    },
    
    // Table Wrapper for mobile scrolling
    {
      id: tableWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Table Wrapper',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: 'pricing-table-wrapper',
      styles: {},
      children: [tableId],
      text: '',
      attributes: {
        'data-element': 'pricing-table-wrapper'
      }
    },
    
    // Table Container
    {
      id: tableId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'table',
      name: 'Pricing Table',
      parentId: tableWrapperId,
      hierarchyName: getHierarchyName('table'),
      className: pricingTableClass,
      styles: {},
      children: [tableHeaderId, tableBodyId],
      text: '',
      attributes: {
        'data-element': 'pricing-table'
      }
    },
    
    // Table Header
    {
      id: tableHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'thead',
      name: 'Table Header',
      parentId: tableId,
      hierarchyName: getHierarchyName('thead'),
      className: pricingTableHeaderClass,
      styles: {},
      children: [tableHeaderRowId],
      text: '',
      attributes: {
        'data-element': 'pricing-table-header'
      }
    },
    
    // Table Header Row
    {
      id: tableHeaderRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tr',
      name: 'Table Header Row',
      parentId: tableHeaderId,
      hierarchyName: getHierarchyName('tr'),
      className: pricingTableHeaderRowClass,
      styles: {},
      children: [emptyHeaderId, basicHeaderId, standardHeaderId, advancedHeaderId],
      text: '',
      attributes: {
        'data-element': 'pricing-table-header-row'
      }
    },
    
    // Empty Header Cell
    {
      id: emptyHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'th',
      name: 'Empty Header',
      parentId: tableHeaderRowId,
      hierarchyName: getHierarchyName('th'),
      className: pricingColumnClass,
      styles: {},
      children: [],
      text: 'Features',
      attributes: {
        'data-element': 'pricing-column-header'
      }
    },
    
    // Basic Plan Header
    {
      id: basicHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'th',
      name: 'Basic Plan Header',
      parentId: tableHeaderRowId,
      hierarchyName: getHierarchyName('th'),
      className: pricingPlanHeaderBasicClass,
      styles: {},
      children: [],
      text: 'Basic Plan',
      attributes: {
        'data-element': 'pricing-column-header',
        'data-plan': 'basic'
      }
    },
    
    // Standard Plan Header
    {
      id: standardHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'th',
      name: 'Standard Plan Header',
      parentId: tableHeaderRowId,
      hierarchyName: getHierarchyName('th'),
      className: pricingPlanHeaderStandardClass,
      styles: {},
      children: [],
      text: 'Standard Plan',
      attributes: {
        'data-element': 'pricing-column-header',
        'data-plan': 'standard'
      }
    },
    
    // Advanced Plan Header
    {
      id: advancedHeaderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'th',
      name: 'Advanced Plan Header',
      parentId: tableHeaderRowId,
      hierarchyName: getHierarchyName('th'),
      className: pricingPlanHeaderAdvancedClass,
      styles: {},
      children: [],
      text: 'Advanced Plan',
      attributes: {
        'data-element': 'pricing-column-header',
        'data-plan': 'advanced'
      }
    },
    
    // Table Body
    {
      id: tableBodyId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tbody',
      name: 'Table Body',
      parentId: tableId,
      hierarchyName: getHierarchyName('tbody'),
      className: pricingTableBodyClass,
      styles: {},
      children: [storageRowId, peopleRowId, fraudRowId, supportRowId, customizationRowId, priceRowId, buttonRowId],
      text: '',
      attributes: {
        'data-element': 'pricing-table-body'
      }
    },
    
    // Price Row (for prices)
    {
      id: priceRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tr',
      name: 'Price Row',
      parentId: tableBodyId,
      hierarchyName: getHierarchyName('tr'),
      className: pricingFeatureRowClass,
      styles: {},
      children: [priceLabelId, basicPriceId, standardPriceId, advancedPriceId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-row'
      }
    },
    
    // Price Label
    {
      id: priceLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Price Label',
      parentId: priceRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureLabelClass,
      styles: {},
      children: [],
      text: 'Choose Your Plan',
      attributes: {
        'data-element': 'pricing-feature-label'
      }
    },
    
    // Basic Price
    {
      id: basicPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Basic Price',
      parentId: priceRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingPlanPriceClass,
      styles: {},
      children: [],
      text: '$ 29',
      attributes: {
        'data-element': 'pricing-plan-price'
      }
    },
    
    // Standard Price
    {
      id: standardPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Standard Price',
      parentId: priceRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingPlanPriceClass,
      styles: {},
      children: [],
      text: '$ 59',
      attributes: {
        'data-element': 'pricing-plan-price'
      }
    },
    
    // Advanced Price
    {
      id: advancedPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Advanced Price',
      parentId: priceRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingPlanPriceClass,
      styles: {},
      children: [],
      text: '$ 139',
      attributes: {
        'data-element': 'pricing-plan-price'
      }
    },
    
    // Storage Row
    {
      id: storageRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tr',
      name: 'Storage Row',
      parentId: tableBodyId,
      hierarchyName: getHierarchyName('tr'),
      className: pricingFeatureRowClass,
      styles: {},
      children: [storageLabelId, basicStorageId, standardStorageId, advancedStorageId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-row'
      }
    },
    
    // Storage Label
    {
      id: storageLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Storage Label',
      parentId: storageRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureLabelClass,
      styles: {},
      children: [],
      text: 'Storage',
      attributes: {
        'data-element': 'pricing-feature-label'
      }
    },
    
    // Basic Storage
    {
      id: basicStorageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Basic Storage',
      parentId: storageRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [],
      text: '10 GB',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Standard Storage
    {
      id: standardStorageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Standard Storage',
      parentId: storageRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [],
      text: '50 GB',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Advanced Storage
    {
      id: advancedStorageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Advanced Storage',
      parentId: storageRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [],
      text: '200 GB',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // People Row
    {
      id: peopleRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tr',
      name: 'People Row',
      parentId: tableBodyId,
      hierarchyName: getHierarchyName('tr'),
      className: pricingFeatureRowClass,
      styles: {},
      children: [peopleLabelId, basicPeopleId, standardPeopleId, advancedPeopleId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-row'
      }
    },
    
    // People Label
    {
      id: peopleLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'People Label',
      parentId: peopleRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureLabelClass,
      styles: {},
      children: [],
      text: 'Number of People',
      attributes: {
        'data-element': 'pricing-feature-label'
      }
    },
    
    // Basic People
    {
      id: basicPeopleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Basic People',
      parentId: peopleRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [],
      text: '1 - 3 People',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Standard People
    {
      id: standardPeopleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Standard People',
      parentId: peopleRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [],
      text: '5 - 10 People',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Advanced People
    {
      id: advancedPeopleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Advanced People',
      parentId: peopleRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [],
      text: 'Unlimited',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Fraud Row
    {
      id: fraudRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tr',
      name: 'Fraud Row',
      parentId: tableBodyId,
      hierarchyName: getHierarchyName('tr'),
      className: pricingFeatureRowClass,
      styles: {},
      children: [fraudLabelId, basicFraudId, standardFraudId, advancedFraudId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-row'
      }
    },
    
    // Fraud Label
    {
      id: fraudLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Fraud Label',
      parentId: fraudRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureLabelClass,
      styles: {},
      children: [],
      text: 'Fraud Analysis',
      attributes: {
        'data-element': 'pricing-feature-label'
      }
    },
    
    // Basic Fraud
    {
      id: basicFraudId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Basic Fraud',
      parentId: fraudRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [basicFraudIconId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Basic Fraud Icon (X)
    {
      id: basicFraudIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Basic Fraud Icon',
      parentId: basicFraudId,
      hierarchyName: getHierarchyName('image'),
      className: pricingXIconClass,
      styles: {},
      src: 'https://api.iconify.design/bx:x.svg?color=%23f56c6c',
      attributes: {
        'data-element': 'pricing-x-icon',
        'alt': 'Not Included'
      }
    },
    
    // Standard Fraud
    {
      id: standardFraudId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Standard Fraud',
      parentId: fraudRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [standardFraudIconId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Standard Fraud Icon (Check)
    {
      id: standardFraudIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Standard Fraud Icon',
      parentId: standardFraudId,
      hierarchyName: getHierarchyName('image'),
      className: pricingCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%2367c23a',
      attributes: {
        'data-element': 'pricing-check-icon',
        'alt': 'Included'
      }
    },
    
    // Advanced Fraud
    {
      id: advancedFraudId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Advanced Fraud',
      parentId: fraudRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [advancedFraudIconId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Advanced Fraud Icon (Check)
    {
      id: advancedFraudIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Advanced Fraud Icon',
      parentId: advancedFraudId,
      hierarchyName: getHierarchyName('image'),
      className: pricingCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%2367c23a',
      attributes: {
        'data-element': 'pricing-check-icon',
        'alt': 'Included'
      }
    },
    
    // Support Row
    {
      id: supportRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tr',
      name: 'Support Row',
      parentId: tableBodyId,
      hierarchyName: getHierarchyName('tr'),
      className: pricingFeatureRowClass,
      styles: {},
      children: [supportLabelId, basicSupportId, standardSupportId, advancedSupportId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-row'
      }
    },
    
    // Support Label
    {
      id: supportLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Support Label',
      parentId: supportRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureLabelClass,
      styles: {},
      children: [],
      text: 'Customer Support',
      attributes: {
        'data-element': 'pricing-feature-label'
      }
    },
    
    // Basic Support
    {
      id: basicSupportId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Basic Support',
      parentId: supportRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [basicSupportIconId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Basic Support Icon (Check)
    {
      id: basicSupportIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Basic Support Icon',
      parentId: basicSupportId,
      hierarchyName: getHierarchyName('image'),
      className: pricingCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%2367c23a',
      attributes: {
        'data-element': 'pricing-check-icon',
        'alt': 'Included'
      }
    },
    
    // Standard Support
    {
      id: standardSupportId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Standard Support',
      parentId: supportRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [standardSupportIconId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Standard Support Icon (Check)
    {
      id: standardSupportIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Standard Support Icon',
      parentId: standardSupportId,
      hierarchyName: getHierarchyName('image'),
      className: pricingCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%2367c23a',
      attributes: {
        'data-element': 'pricing-check-icon',
        'alt': 'Included'
      }
    },
    
    // Advanced Support
    {
      id: advancedSupportId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Advanced Support',
      parentId: supportRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [advancedSupportIconId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Advanced Support Icon (Check)
    {
      id: advancedSupportIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Advanced Support Icon',
      parentId: advancedSupportId,
      hierarchyName: getHierarchyName('image'),
      className: pricingCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%2367c23a',
      attributes: {
        'data-element': 'pricing-check-icon',
        'alt': 'Included'
      }
    },
    
    // Customization Row
    {
      id: customizationRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tr',
      name: 'Customization Row',
      parentId: tableBodyId,
      hierarchyName: getHierarchyName('tr'),
      className: pricingFeatureRowClass,
      styles: {},
      children: [customizationLabelId, basicCustomizationId, standardCustomizationId, advancedCustomizationId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-row'
      }
    },
    
    // Customization Label
    {
      id: customizationLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Customization Label',
      parentId: customizationRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureLabelClass,
      styles: {},
      children: [],
      text: 'Customization',
      attributes: {
        'data-element': 'pricing-feature-label'
      }
    },
    
    // Basic Customization
    {
      id: basicCustomizationId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Basic Customization',
      parentId: customizationRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [basicCustomizationIconId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Basic Customization Icon (X)
    {
      id: basicCustomizationIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Basic Customization Icon',
      parentId: basicCustomizationId,
      hierarchyName: getHierarchyName('image'),
      className: pricingXIconClass,
      styles: {},
      src: 'https://api.iconify.design/bx:x.svg?color=%23f56c6c',
      attributes: {
        'data-element': 'pricing-x-icon',
        'alt': 'Not Included'
      }
    },
    
    // Standard Customization
    {
      id: standardCustomizationId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Standard Customization',
      parentId: customizationRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [standardCustomizationIconId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Standard Customization Icon (X)
    {
      id: standardCustomizationIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Standard Customization Icon',
      parentId: standardCustomizationId,
      hierarchyName: getHierarchyName('image'),
      className: pricingXIconClass,
      styles: {},
      src: 'https://api.iconify.design/bx:x.svg?color=%23f56c6c',
      attributes: {
        'data-element': 'pricing-x-icon',
        'alt': 'Not Included'
      }
    },
    
    // Advanced Customization
    {
      id: advancedCustomizationId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Advanced Customization',
      parentId: customizationRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [advancedCustomizationIconId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Advanced Customization Icon (Check)
    {
      id: advancedCustomizationIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Advanced Customization Icon',
      parentId: advancedCustomizationId,
      hierarchyName: getHierarchyName('image'),
      className: pricingCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-outline.svg?color=%2367c23a',
      attributes: {
        'data-element': 'pricing-check-icon',
        'alt': 'Included'
      }
    },
    
    // Button Row (for buttons)
    {
      id: buttonRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tr',
      name: 'Button Row',
      parentId: tableBodyId,
      hierarchyName: getHierarchyName('tr'),
      className: pricingFeatureRowClass,
      styles: {},
      children: [buttonLabelId, basicButtonId, standardButtonId, advancedButtonId],
      text: '',
      attributes: {
        'data-element': 'pricing-button-row'
      }
    },
    
    // Button Label
    {
      id: buttonLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Button Label',
      parentId: buttonRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureLabelClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-label'
      }
    },
    
    // Basic Button
    {
      id: basicButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Basic Button Cell',
      parentId: buttonRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [basicButtonContentId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Basic Button Content
    {
      id: basicButtonContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Basic Button',
      parentId: basicButtonId,
      hierarchyName: getHierarchyName('button'),
      className: pricingPlanButtonClass,
      styles: {},
      children: [],
      text: 'CHOOSE PLAN',
      attributes: {
        'data-element': 'pricing-plan-button'
      }
    },
    
    // Standard Button
    {
      id: standardButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Standard Button Cell',
      parentId: buttonRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [standardButtonContentId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Standard Button Content
    {
      id: standardButtonContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Standard Button',
      parentId: standardButtonId,
      hierarchyName: getHierarchyName('button'),
      className: pricingPlanButtonClass,
      styles: {},
      children: [],
      text: 'CHOOSE PLAN',
      attributes: {
        'data-element': 'pricing-plan-button'
      }
    },
    
    // Advanced Button
    {
      id: advancedButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'td',
      name: 'Advanced Button Cell',
      parentId: buttonRowId,
      hierarchyName: getHierarchyName('td'),
      className: pricingFeatureItemClass,
      styles: {},
      children: [advancedButtonContentId],
      text: '',
      attributes: {
        'data-element': 'pricing-feature-item'
      }
    },
    
    // Advanced Button Content
    {
      id: advancedButtonContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Advanced Button',
      parentId: advancedButtonId,
      hierarchyName: getHierarchyName('button'),
      className: pricingPlanButtonClass,
      styles: {},
      children: [],
      text: 'CHOOSE PLAN',
      attributes: {
        'data-element': 'pricing-plan-button'
      }
    }
  ];
  
  return {
    pricingSection: newElements[0],
    elements: newElements
  };
};

export default createPricingElements; 